/**
 * CartDrawer.jsx - Shopping Cart Slide-out Drawer
 * 
 * Features:
 * - Slide-out cart panel from right side
 * - Add/remove items, adjust quantities
 * - Email collection (required before checkout)
 * - Shipping info display (UK, International, pen surcharges)
 * - WhatsApp checkout integration
 * - Saves order to backend API when user is logged in
 */
import React, { useState } from 'react';
import { X, Plus, Minus, Trash2, MessageCircle, Mail, FlaskConical } from 'lucide-react';
import { Button } from '../ui/button';
import { useCart } from '../../contexts/CartContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { ScrollArea } from '../ui/scroll-area';

const CartDrawer = () => {
  const {
    cartItems,
    cartTotal,
    cartCount,
    isCartOpen,
    setIsCartOpen,
    updateQuantity,
    removeFromCart,
    clearCart
  } = useCart();

  // Email state for order collection
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [disclaimerError, setDisclaimerError] = useState('');

  /** Validate email format */
  const validateEmail = (e) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(e);
  };

  /**
   * Handle checkout:
   * 1. Validate email
   * 2. Build WhatsApp message
   * 3. Save order to backend (if logged in)
   * 4. Open WhatsApp
   */
  const handleCheckout = () => {
    if (cartItems.length === 0) return;

    if (!email.trim()) {
      setEmailError('Email is required to place an order');
      return;
    }
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    if (!disclaimerAccepted) {
      setDisclaimerError('You must accept the research use disclaimer to proceed');
      return;
    }
    setEmailError('');
    setDisclaimerError('');

    const itemsList = cartItems.map(item =>
      `- ${item.name}${item.selectedOption ? ` (${item.selectedOption})` : ''} x${item.quantity} = £${(item.price * item.quantity).toFixed(2)}`
    ).join('\n');

    const message = `🛒 *New Order from Forged Supps*\n\n📧 ${email}\n\n${itemsList}\n\n*Subtotal: £${cartTotal.toFixed(2)}*\n_Shipping: UK £10 (1-2 days) | International £15_\n\nPlease confirm availability and payment details.`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/447440352811?text=${encodedMessage}`;

    // Save email to collection for marketing
    fetch('/api/emails', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email, source: 'checkout' })
    }).catch(() => {});

    // Save order to backend for records
    const token = localStorage.getItem('token');
    if (token) {
      fetch('/api/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          items: cartItems.map(item => ({
            productId: item.id,
            name: item.name,
            quantity: item.quantity,
            price: item.price,
            selectedOption: item.selectedOption || null
          })),
          total: cartTotal,
          email: email
        })
      }).catch(() => {});
    }

    window.open(whatsappUrl, '_blank');
  };

  return (
    <Sheet open={isCartOpen} onOpenChange={setIsCartOpen}>
      <SheetContent className="bg-zinc-900 border-zinc-800 w-full sm:max-w-lg flex flex-col">
        <SheetHeader>
          <SheetTitle className="text-white flex items-center justify-between">
            <span>Your Cart ({cartCount})</span>
            {cartItems.length > 0 && (
              <Button variant="ghost" size="sm" onClick={clearCart} className="text-zinc-400 hover:text-red-400">
                Clear all
              </Button>
            )}
          </SheetTitle>
        </SheetHeader>

        {cartItems.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-20 h-20 bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <X className="h-10 w-10 text-zinc-600" />
              </div>
              <p className="text-zinc-400">Your cart is empty</p>
            </div>
          </div>
        ) : (
          <>
            <ScrollArea className="flex-1 -mx-6 px-6">
              <div className="space-y-4 py-4">
                {cartItems.map((item) => (
                  <div key={item.cartId} className="flex gap-4 bg-zinc-800 rounded-lg p-3">
                    <img src={item.image} alt={item.name} className="w-20 h-20 object-cover rounded-lg" />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white font-medium truncate">{item.name}</h4>
                      {item.selectedOption && <p className="text-zinc-400 text-sm">{item.selectedOption}</p>}
                      <p className="text-amber-500 font-semibold">£{item.price.toFixed(2)}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Button variant="outline" size="icon" className="h-8 w-8 border-zinc-700" onClick={() => updateQuantity(item.cartId, item.quantity - 1)}>
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="text-white w-8 text-center">{item.quantity}</span>
                        <Button variant="outline" size="icon" className="h-8 w-8 border-zinc-700" onClick={() => updateQuantity(item.cartId, item.quantity + 1)}>
                          <Plus className="h-3 w-3" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 ml-auto text-zinc-400 hover:text-red-400" onClick={() => removeFromCart(item.cartId)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            <div className="border-t border-zinc-800 pt-4 space-y-4">
              {/* Shipping info */}
              <div className="bg-zinc-800 rounded-lg p-3 space-y-1">
                <p className="text-zinc-300 text-sm font-medium">📦 Shipping</p>
                <p className="text-zinc-400 text-sm">UK: £10.00 (1-2 days)</p>
                <p className="text-zinc-400 text-sm">International: £15.00 (tracked & signed)</p>
                <p className="text-zinc-500 text-xs mt-1">Pens (Reta, Tirz, Glow): +£25 each</p>
              </div>

              {/* Email - required for checkout */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Mail className="h-4 w-4 text-zinc-400" />
                  <label className="text-zinc-300 text-sm font-medium">Email address</label>
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
                  placeholder="your@email.com"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white placeholder-zinc-500 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                />
                {emailError && <p className="text-red-400 text-xs mt-1">{emailError}</p>}
              </div>

              {/* Total */}
              <div className="flex justify-between items-center">
                <span className="text-zinc-400">Subtotal</span>
                <span className="text-white text-2xl font-bold">£{cartTotal.toFixed(2)}</span>
              </div>

              {/* Research Disclaimer Checkbox */}
              <div className="bg-zinc-800/50 rounded-lg p-3 border border-zinc-700/50">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={disclaimerAccepted}
                    onChange={(e) => { setDisclaimerAccepted(e.target.checked); setDisclaimerError(''); }}
                    className="mt-0.5 h-4 w-4 rounded border-zinc-600 bg-zinc-700 text-amber-500 focus:ring-amber-500 focus:ring-offset-0 flex-shrink-0"
                  />
                  <span className="text-zinc-400 text-xs leading-relaxed">
                    <FlaskConical className="h-3 w-3 text-amber-500 inline mr-1" />
                    I confirm these products are for <span className="text-zinc-300 font-medium">research purposes only</span>, not for human or animal consumption, and I am <span className="text-zinc-300 font-medium">18 years or older</span>. I agree to the{' '}
                    <a href="/terms" target="_blank" className="text-amber-500 hover:underline">Terms &amp; Disclaimer</a>.
                  </span>
                </label>
                {disclaimerError && <p className="text-red-400 text-xs mt-2 ml-7">{disclaimerError}</p>}
              </div>

              {/* Checkout */}
              <Button className="w-full h-12 font-semibold text-lg bg-green-600 hover:bg-green-700 text-white" onClick={handleCheckout}>
                <MessageCircle className="h-5 w-5 mr-2" />
                Checkout via WhatsApp
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
